import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Header from '../Header';
import Sidebar from '../Sidebar';
import MobileBottomNav from '../MobileBottomNav';
import SavvyBot from '../SavvyBot';
import OnboardingDeckModal from '../OnboardingDeckModal';
import { setSbOpen } from '../../redux/uiReducer';
import { BillingNotificationSessionProvider } from '../../context/BillingNotificationSession';
import './index.scss';

const BOT_MIN = 280;
const APP_MIN = 500;
const LS_KEY = 'savvybot-panel-width';
const DEFAULT_WIDTH = 380;
const KEYBOARD_STEP = 20;
const COLLAPSED_KEY = 'sidebar-collapsed';

function clampBotWidth(w, containerWidth) {
  const maxW = containerWidth - APP_MIN;
  return Math.max(BOT_MIN, Math.min(w, maxW));
}

const Layout = () => {
  const location = useLocation();
  const isHelpPage = /^\/(?:client\/)?help/.test(location.pathname);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 901);
  const [sidebarCollapsedPref, setSidebarCollapsedPref] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const sidebarCollapsed = sidebarCollapsedPref && isDesktop;
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsedPref((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  }, []);

  const dispatch = useDispatch();
  const sbOpen = useSelector((state) => state.ui.sbOpen);
  const handleToggleSb = useCallback(() => dispatch(setSbOpen(!sbOpen)), [dispatch, sbOpen]);
  const handleCloseSb = useCallback(() => dispatch(setSbOpen(false)), [dispatch]);

  const headerWrapRef = useRef(null);
  const layoutMainRef = useRef(null);
  const splitRootRef = useRef(null);
  const dividerRef = useRef(null);
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(DEFAULT_WIDTH);

  const [botWidth, setBotWidth] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) return parseInt(stored, 10);
    } catch {}
    return DEFAULT_WIDTH;
  });

  const persistWidth = useCallback((w) => {
    setBotWidth(w);
    try {
      localStorage.setItem(LS_KEY, String(w));
    } catch {}
  }, []);

  useEffect(() => {
    const headerEl = headerWrapRef.current?.querySelector('.main-header-sticky');
    const mainEl = layoutMainRef.current;
    if (!headerEl || !mainEl || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const h = headerEl.getBoundingClientRect().height;
      mainEl.style.setProperty('--mobile-header-h', `${Math.ceil(h)}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(headerEl);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const onMouseMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      const container = splitRootRef.current;
      if (!container) return;
      const containerWidth = container.getBoundingClientRect().width;
      const delta = dragStartX.current - e.clientX;
      const newW = clampBotWidth(dragStartWidth.current + delta, containerWidth);
      persistWidth(newW);
    },
    [persistWidth],
  );

  const onMouseUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    document.body.classList.remove('savvybot-resizing');
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      dragging.current = true;
      dragStartX.current = e.clientX;
      dragStartWidth.current = botWidth;
      document.body.classList.add('savvybot-resizing');
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [botWidth, onMouseMove, onMouseUp],
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      const container = splitRootRef.current;
      if (!container) return;
      const containerWidth = container.getBoundingClientRect().width;
      const touch = e.touches[0];
      const delta = dragStartX.current - touch.clientX;
      const newW = clampBotWidth(dragStartWidth.current + delta, containerWidth);
      persistWidth(newW);
    },
    [persistWidth],
  );

  const onTouchEnd = useCallback(() => {
    dragging.current = false;
    document.body.classList.remove('savvybot-resizing');
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  }, [onTouchMove]);

  const onTouchStart = useCallback(
    (e) => {
      dragging.current = true;
      dragStartX.current = e.touches[0].clientX;
      dragStartWidth.current = botWidth;
      document.body.classList.add('savvybot-resizing');
      window.addEventListener('touchmove', onTouchMove, { passive: true });
      window.addEventListener('touchend', onTouchEnd);
    },
    [botWidth, onTouchMove, onTouchEnd],
  );

  const onKeyDown = useCallback(
    (e) => {
      const container = splitRootRef.current;
      if (!container) return;
      const containerWidth = container.getBoundingClientRect().width;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        persistWidth(clampBotWidth(botWidth + KEYBOARD_STEP, containerWidth));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        persistWidth(clampBotWidth(botWidth - KEYBOARD_STEP, containerWidth));
      }
    },
    [botWidth, persistWidth],
  );

  return (
    <>
      <OnboardingDeckModal />
      <BillingNotificationSessionProvider>
        <div
          className={`app-split-root${sbOpen ? ' app-split-root--open' : ''}${isHelpPage ? ' app-split-root--help' : ''}`}
          ref={splitRootRef}>
          <div className="d-flex flex-column flex-fill app-left-col" ref={headerWrapRef}>
            <Header
              sbOpen={sbOpen}
              onToggleSb={handleToggleSb}
              sidebarCollapsed={sidebarCollapsed}
            />
            <div className="d-flex flex-fill layout-body">
              {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}
              <Sidebar
                isOpen={sidebarOpen}
                onClose={closeSidebar}
                isCollapsed={sidebarCollapsed}
                onToggleCollapsed={toggleSidebarCollapsed}
              />
              <div className="d-flex flex-column flex-fill layout-main" ref={layoutMainRef}>
                <div className="layout-scroll">
                  <Outlet />
                </div>
              </div>
            </div>
            <MobileBottomNav
              onMenuToggle={toggleSidebar}
              sbOpen={sbOpen}
              onToggleSb={handleToggleSb}
            />
          </div>
          {sbOpen && (
            <>
              <div
                ref={dividerRef}
                className="savvybot-divider"
                role="separator"
                aria-label="Resize SavvyBot panel"
                aria-orientation="vertical"
                tabIndex={0}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
                onKeyDown={onKeyDown}
              />
              <div className="savvybot-column" style={{ width: botWidth }}>
                <SavvyBot columnMode newChatSignal={0} onClose={handleCloseSb} />
              </div>
            </>
          )}
        </div>
      </BillingNotificationSessionProvider>
    </>
  );
};

export default Layout;
